import {rootState} from '@mintdata-wsp/common/state/RootState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {MLogo} from '@mintdata-wsp/wsp/ui/components/logo/MLogo';
import {SurfaceKeyboardShortcuts} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceKeyboardShortcuts';
import {TopBarCollapsedMenuButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarCollapsedMenuButton';
import {TopBarExpandedMenuButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarExpandedMenuButton';
import {TopBarPagesButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarPagesButton';
import {TopBarPreviewButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarPreviewButton';
import {TopBarPropertiesButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarPropertiesButton';
import {TopBarPublishButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarPublishButton';
import {TopBarSpreadsheetButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/buttons/TopBarSpreadsheetButton';
import {TopBarSeparator} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/TopBarSeparator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';
import {TopBarUserIconList} from '@mintdata-wsp/wsp/ui/views/visualDesigner/header/userPresence/TopBarUserIconList';
import {UserIconSize} from '@mintdata-wsp/wsp/ui/views/visualDesigner/header/userPresence/UserIcon';
import {SyncStatus} from '../header/syncStatus/SyncStatus';

const styles = require('./TopBar.less');
const cx = classnames.bind(styles);

export interface ITopBarProps
{
    isGhost?:boolean;
}

@observer
export class TopBar extends React.Component<ITopBarProps, any>
{
    public static defaultProps:ITopBarProps = {
        isGhost: false
    };

    private _refHandlers = {
        topBarLeft: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setTopBarLeftDomRef(ref),
        topBarMiddle: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setTopBarMiddleDomRef(ref),
        topBarMiddleZoom: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setTopBarMiddleZoomDomRef(ref),
        topBarRight: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setTopBarRightDomRef(ref)
    };

    private _renderLeftBar():JSX.Element
    {
        const {topMenu, openDesign} = controllers.ui;

        const isSampleDesign:boolean = openDesign.isInSampleDesignMode();
        const isTopMenuExpanded:boolean = topMenu.isTopMenuExpanded();
        const isCollapsingTransitionInProgress:boolean = topMenu.isCollapsingTransitionInProgress();
        const showCollapsedMenu:boolean = !isTopMenuExpanded && !isCollapsingTransitionInProgress;
        // const showCollapsedMenu:boolean = !isTopMenuExpanded;

        return (
            <div
                ref={this._refHandlers.topBarLeft}
                className={cx('leftBar')}
            >
                <div
                    className={cx('logoAndCollapsedMenuButton', {
                        isVisible: showCollapsedMenu,
                        isSampleDesign
                    })}
                    onTransitionEnd={() => topMenu.endTopMenuLogoTransition()}
                >
                    <MLogo tooltipProps={{content: 'Go to Workspace'}} iconClassName={cx('logoIcon')} />
                    {
                        !isSampleDesign &&
                        <div className={cx('barButton', 'collapsedMenuButton')}>
                            <TopBarCollapsedMenuButton />
                        </div>
                    }
                </div>
                <div className={cx('barButton', 'pagesButton')}>
                    <TopBarPagesButton />
                </div>
            </div>
        );
    }

    private _renderMiddleBar():JSX.Element
    {
        const {surfaceEditor: {area: {zoom}}} = rootState.ui.visualDesigner;

        return (
            <div className={cx('middleBarWrapper')}>

                <div className={cx('middleBarLeft')} />

                <div
                    ref={this._refHandlers.topBarMiddle}
                    className={cx('middleBar')}
                >

                    <div className={cx('barButton')}>
                        <TopBarPreviewButton />
                    </div>

                </div>

                <div className={cx('middleBarRight')}>
                    <div
                        ref={this._refHandlers.topBarMiddleZoom}
                    >
                        {
                            zoom !== 1 &&
                            <span className={cx('zoomLabel')}>
                                {(zoom * 100).toFixed(0)}%
                            </span>
                        }
                    </div>
                    {
                        !controllers.ui.topMenu.isTopMenuExpanded() &&
                            <TopBarUserIconList iconSize={UserIconSize.Small} className={cx('userIconsRight')} />
                    }
                </div>

            </div>
        );
    }

    private _renderRightBar():JSX.Element
    {
        const {workbenchVisibility, topMenu, openDesign} = controllers.ui;

        const isSampleDesign:boolean = openDesign.isInSampleDesignMode();

        const isTopMenuExpanded:boolean = topMenu.isTopMenuExpanded();
        const isCollapsingTransitionInProgress:boolean = topMenu.isCollapsingTransitionInProgress();
        const showCollapsedMenu:boolean = !isTopMenuExpanded && !isCollapsingTransitionInProgress;
        // const showCollapsedMenu:boolean = !isTopMenuExpanded;

        return (
            <div
                ref={this._refHandlers.topBarRight}
                className={cx('rightBar')}
            >
                {!isTopMenuExpanded && <SyncStatus minimal />}
                {
                    !isSampleDesign &&
                    <div className={cx('publishButtonAndSeparator', {isVisible: showCollapsedMenu})}>
                        <div className={cx('barButton')}>
                            <TopBarPublishButton />
                        </div>
                        <TopBarSeparator />
                    </div>
                }
                <div className={cx('barButton', {isVisible: showCollapsedMenu})}>
                    <TopBarPropertiesButton />
                </div>
                {
                    !workbenchVisibility.isSpreadsheetVisible() &&
                    <div className={cx('barButton', 'spreadsheetButton')}>
                        <TopBarSpreadsheetButton />
                    </div>
                }
                {
                    !workbenchVisibility.isSpreadsheetVisible() && !isSampleDesign &&
                    <div className={cx('barButton', 'expandedMenuButton')}>
                        <TopBarExpandedMenuButton />
                    </div>
                }
                {
                    !workbenchVisibility.isSpreadsheetVisible() && !isSampleDesign &&
                    <div className={cx('lxPlaceholder', {isTopMenuExpanded})}></div>
                }
            </div>
        );
    }

    render():JSX.Element
    {
        const {workbenchVisibility, openDesign} = controllers.ui;

        const {isGhost} = this.props;

        if( isGhost )
        {
            return (
                <div className={cx('base')} />
            );
        }

        const isSpreadsheetVisible = workbenchVisibility.isSpreadsheetVisible();

        return (

            <SurfaceKeyboardShortcuts className={cx('base', {
                spreadsheetVisible: isSpreadsheetVisible,
                isSampleDesign: openDesign.isInSampleDesignMode()
            })}>
                {this._renderLeftBar()}
                {this._renderMiddleBar()}
                {this._renderRightBar()}
            </SurfaceKeyboardShortcuts>

        );
    }
}
